import React, {useState, useEffect} from 'react';
import {Row, Col, Label, Input, FormGroup, FormFeedback, Button, Spinner, Card, CardBody} from 'reactstrap';
import {useDispatch} from 'react-redux';
import {getCustomersFindOne} from "../../../slices/customers/thunk";
import {toast} from 'react-toastify';

const CustomerInfo = ({formik, documentTypes}: any) => {
    const [isSearching, setIsSearching] = useState(false);
    const dispatch: any = useDispatch();

    useEffect(() => {
        const selectedDocType = documentTypes.find((type: any) => type.id === formik.values.buyerIdType);

        if (selectedDocType && selectedDocType.name.toUpperCase() === 'VENTA A CONSUMIDOR FINAL') {
            if (formik.values.buyerId !== '9999999999999' || formik.values.buyerName !== 'CONSUMIDOR FINAL') {
                formik.setFieldValue('buyerId', '9999999999999', false);
                formik.setFieldError('buyerId', undefined);
                formik.setFieldTouched('buyerId', false);
                formik.setFieldValue('buyerName', 'CONSUMIDOR FINAL');
                formik.setFieldValue('buyerAddress', 'S/N');
                formik.setFieldValue('buyerPhone', '');
                formik.setFieldValue('buyerEmail', '');
                formik.setFieldValue('id', '');
            }
        }
    }, [formik.values.buyerIdType, documentTypes, formik]);

    const isConsumerFinal = () => {
        const selectedDocType = documentTypes.find((type: any) => type.id === formik.values.buyerIdType);
        return selectedDocType && selectedDocType.name.toUpperCase() === 'VENTA A CONSUMIDOR FINAL';
    };

    const getMaxLength = () => {
        const selectedDocType = documentTypes.find((type: any) => type.id === formik.values.buyerIdType);
        if (!selectedDocType) return 13;
        const docName = selectedDocType.name.toUpperCase();
        if (docName.includes('CÉDULA')) return 10;
        if (docName.includes('RUC')) return 13;
        if (docName.includes('PASAPORTE')) return 20;
        return 20;
    };

    const validateIdentification = (value: string) => {
        if (isConsumerFinal()) return;
        const selectedDocType = documentTypes.find((type: any) => type.id === formik.values.buyerIdType);
        if (!selectedDocType) return;

        const docName = selectedDocType.name.toUpperCase();

        if (docName.includes('CÉDULA')) {
            if (value.length !== 10) {
                formik.setFieldError('buyerId', 'La cédula debe tener 10 dígitos');
                formik.setFieldTouched('buyerId', true, false);
            } else {
                let total = 0;
                const length = value.length;
                const longcheck = length - 1;

                if (value !== "" && length === 10) {
                    for (let i = 0; i < longcheck; i++) {
                        if (i % 2 === 0) {
                            let aux = parseInt(value.charAt(i)) * 2;
                            if (aux > 9) aux -= 9;
                            total += aux;
                        } else {
                            total += parseInt(value.charAt(i));
                        }
                    }

                    total = total % 10 ? 10 - total % 10 : 0;

                    if (parseInt(value.charAt(length - 1)) !== total) {
                        formik.setFieldError('buyerId', 'Cédula inválida');
                        formik.setFieldTouched('buyerId', true, false);
                    } else {
                        if (formik.errors.buyerId === 'Cédula inválida' || formik.errors.buyerId === 'La cédula debe tener 10 dígitos') {
                            formik.setFieldError('buyerId', undefined);
                        }
                    }
                }
            }
        } else if (docName.includes('RUC')) {
            if (value.length !== 13) {
                formik.setFieldError('buyerId', 'El RUC debe tener 13 dígitos');
                formik.setFieldTouched('buyerId', true, false);
            } else if (!value.endsWith('001')) {
                formik.setFieldError('buyerId', 'El RUC debe terminar en 001');
                formik.setFieldTouched('buyerId', true, false);
            } else {
                if (formik.errors.buyerId) {
                    formik.setFieldError('buyerId', undefined);
                }
            }
        } else if (docName.includes('PASAPORTE')) {
            if (value.length < 5) {
                formik.setFieldError('buyerId', 'El pasaporte debe tener al menos 5 caracteres');
                formik.setFieldTouched('buyerId', true, false);
            } else {
                if (formik.errors.buyerId) {
                    formik.setFieldError('buyerId', undefined);
                }
            }
        }
    };

    const clearCustomerFields = () => {
        formik.setFieldValue('id', '');
        formik.setFieldValue('buyerName', '');
        formik.setFieldValue('buyerAddress', '');
        formik.setFieldValue('buyerPhone', '');
        formik.setFieldValue('buyerEmail', '');
    };

    const fillCustomerData = (customer: any) => {
        formik.setFieldValue("id", customer.id || "");
        formik.setFieldValue("buyerName", customer.name || "");
        formik.setFieldValue("buyerAddress", customer.address || "");
        formik.setFieldValue("buyerPhone", customer.phone || "");
        formik.setFieldValue("buyerEmail", customer.email || "");
        if (customer.identityDocumentTypeId) {
            formik.setFieldValue("buyerIdType", customer.identityDocumentTypeId);
        }
    };

    const handleIdChangeLocal = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        const selectedDocType = documentTypes.find((type: any) => type.id === formik.values.buyerIdType);
        if (selectedDocType) {
            const docName = selectedDocType.name.toUpperCase();
            if (docName.includes('CÉDULA') || docName.includes('RUC')) {
                if (!/^\d*$/.test(value)) return;
            }
        }

        if (!formik.values.buyerIdType) {
            formik.setFieldTouched('buyerIdType', true, false);
            formik.setFieldError('buyerIdType', 'Tipo de identificación es requerido');
        }

        formik.handleChange(e);

        if (value === '') {
            clearCustomerFields();
        } else {
            if (formik.values.id) {
                formik.setFieldValue('id', '');
            }
        }
        validateIdentification(value);
    };

    const searchOnServer = async (identification: string) => {
        if (!identification || formik.errors.buyerId) return;

        setIsSearching(true);

        try {
            const result = await dispatch(getCustomersFindOne({
                identificationNumber: identification, isCustomer: true
            })).unwrap();

            let foundCustomer = null;
            if (Array.isArray(result)) {
                foundCustomer = result.find((c: any) => c.identificationNumber === identification);
            } else if (result && typeof result === 'object') {
                if (result.identificationNumber === identification) {
                    foundCustomer = result;
                }
            }

            if (foundCustomer) {
                fillCustomerData(foundCustomer);
                toast.success("Cliente encontrado");
            } else {
                toast.info("Cliente no encontrado. Puede registrarlo.");
                clearCustomerFields();
            }
        } catch (error) {
            console.error("Error buscando cliente:", error);
            toast.error("Error al buscar el cliente. Intente de nuevo.");
            clearCustomerFields();
        } finally {
            setIsSearching(false);
        }
    };

    return (
        <Card className="shadow border-0 h-100 overflow-hidden rounded-3 mb-4">
            <div className="bg-soft-primary p-3 border-bottom border-light">
                <h5 className="card-title mb-0 text-primary fw-semibold d-flex align-items-center">
                    <i className="ri-user-3-fill me-2 fs-18"></i>
                    Datos del Cliente
                </h5>
            </div>
            <CardBody className="p-4 bg-white">
                <Row className="g-4">
                    <Col lg={6} md={12}>
                        <Row className="g-3">
                            <Col xs={7}>
                                <FormGroup className="mb-0">
                                    <Label className="form-label">Identificación <span className="text-danger">*</span></Label>
                                    <div className="d-flex gap-2">
                                        <div className="flex-grow-1">
                                            <div className="input-group flex-nowrap">
                                                <span className="input-group-text bg-light border-end-0"><i className="ri-profile-line text-muted"></i></span>
                                                <Input
                                                    name="buyerId"
                                                    value={formik.values.buyerId}
                                                    onChange={handleIdChangeLocal}
                                                    onBlur={formik.handleBlur}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            searchOnServer(formik.values.buyerId);
                                                        }
                                                    }}
                                                    invalid={!!(formik.touched.buyerId && formik.errors.buyerId)}
                                                    placeholder={
                                                        !formik.values.buyerIdType
                                                            ? "Seleccione primero el tipo de identificación"
                                                            : "Ingrese ID"
                                                    }
                                                    disabled={isConsumerFinal()}
                                                    maxLength={getMaxLength()}
                                                    className="border-start-0"
                                                />
                                            </div>
                                            <FormFeedback>
                                                {!formik.values.buyerIdType
                                                    ? "Debe seleccionar un tipo de identificación primero"
                                                    : formik.errors.buyerId
                                                }
                                            </FormFeedback>
                                        </div>
                                        <Button
                                            color="primary"
                                            className="flex-shrink-0"
                                            onClick={() => searchOnServer(formik.values.buyerId)}
                                            disabled={isSearching || !formik.values.buyerId || !!formik.errors.buyerId || isConsumerFinal()}
                                        >
                                            {isSearching ? (
                                                <>
                                                    <Spinner size="sm" className="me-1"/>
                                                    Buscando...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="ri-search-line me-1"></i>
                                                    Buscar
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </FormGroup>
                            </Col>

                            <Col xs={5}>
                                <FormGroup className="mb-0">
                                    <Label className="form-label text-nowrap">Tipo Identificación <span className="text-danger">*</span></Label>
                                    <div className="input-group flex-nowrap">
                                        <span className="input-group-text bg-light border-end-0"><i className="ri-profile-line text-muted"></i></span>
                                        <Input
                                            type="select"
                                            name="buyerIdType"
                                            value={formik.values.buyerIdType}
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                const previousValue = formik.values.buyerIdType;

                                                formik.handleChange(e);

                                                const isComingFromConsumerFinal = previousValue &&
                                                    documentTypes.find((t: any) => t.id === previousValue)?.name.toUpperCase() === 'VENTA A CONSUMIDOR FINAL';
                                                const isGoingToConsumerFinal = newValue &&
                                                    documentTypes.find((t: any) => t.id === newValue)?.name.toUpperCase() === 'VENTA A CONSUMIDOR FINAL';

                                                if (isComingFromConsumerFinal || isGoingToConsumerFinal) {
                                                    formik.setFieldValue('buyerId', '');
                                                    clearCustomerFields();
                                                }
                                            }}
                                            invalid={!!(formik.touched.buyerIdType && formik.errors.buyerIdType)}
                                            className="form-select border-start-0"
                                        >
                                            <option value="">Seleccione...</option>
                                            {documentTypes.map((type: any) => (
                                                <option key={type.id} value={type.id}>
                                                    {type.name || type.description || type.code}
                                                </option>
                                            ))}
                                        </Input>
                                    </div>
                                    <FormFeedback>{formik.errors.buyerIdType}</FormFeedback>
                                </FormGroup>
                            </Col>
                        </Row>
                    </Col>

                    <Col lg={6} md={12}>
                        <FormGroup className="mb-0">
                            <Label className="form-label">Razón Social / Nombres <span className="text-danger">*</span></Label>
                            <div className="input-group flex-nowrap">
                                <span className="input-group-text bg-light border-end-0"><i className="ri-user-line text-muted"></i></span>
                                <Input
                                    name="buyerName"
                                    value={formik.values.buyerName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    invalid={!!(formik.touched.buyerName && formik.errors.buyerName)}
                                    disabled={isConsumerFinal()}
                                    className="border-start-0"
                                />
                            </div>
                            <FormFeedback>{formik.errors.buyerName}</FormFeedback>
                        </FormGroup>
                    </Col>
                </Row>

                <Row className="g-4">

                    <Col lg={6} md={12}>
                        <FormGroup className="mb-0">
                            <Label className="form-label">Dirección</Label>
                            <div className="input-group flex-nowrap">
                                <span className="input-group-text bg-light border-end-0"><i className="ri-map-pin-line text-muted"></i></span>
                                <Input
                                    name="buyerAddress"
                                    value={formik.values.buyerAddress}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    invalid={!!(formik.touched.buyerAddress && formik.errors.buyerAddress)}
                                    disabled={isConsumerFinal()}
                                    className="border-start-0"
                                />
                            </div>
                            <FormFeedback>{formik.errors.buyerAddress}</FormFeedback>
                        </FormGroup>
                    </Col>

                    <Col lg={6} md={12}>
                        <Row className="g-3">
                            <Col xs={6}>
                                <FormGroup className="mb-0">
                                    <Label className="form-label">Teléfono</Label>
                                    <div className="input-group flex-nowrap">
                                        <span className="input-group-text bg-light border-end-0"><i className="ri-phone-line text-muted"></i></span>
                                        <Input
                                            name="buyerPhone"
                                            value={formik.values.buyerPhone}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (/^[\d\s+]*$/.test(val)) {
                                                    if (val.length <= 13) {
                                                        formik.handleChange(e);
                                                    }
                                                }
                                            }}
                                            onBlur={(e) => {
                                                formik.handleBlur(e);
                                                const val = e.target.value;
                                                if (val && val.replace(/\D/g, '').length > 10) {
                                                    formik.setFieldError('buyerPhone', 'El teléfono no debe tener más de 10 dígitos numéricos');
                                                }
                                            }}
                                            invalid={!!(formik.touched.buyerPhone && formik.errors.buyerPhone)}
                                            disabled={isConsumerFinal()}
                                            className="border-start-0"
                                        />
                                    </div>
                                    <FormFeedback>{formik.errors.buyerPhone}</FormFeedback>
                                </FormGroup>
                            </Col>

                            <Col xs={6}>
                                <FormGroup className="mb-0">
                                    <Label className="form-label">Correo Electrónico</Label>
                                    <div className="input-group flex-nowrap">
                                        <span className="input-group-text bg-light border-end-0"><i className="ri-mail-line text-muted"></i></span>
                                        <Input
                                            type="email"
                                            name="buyerEmail"
                                            value={formik.values.buyerEmail}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            invalid={!!(formik.touched.buyerEmail && formik.errors.buyerEmail)}
                                            disabled={isConsumerFinal()}
                                            className="border-start-0"
                                        />
                                    </div>
                                    <FormFeedback>{formik.errors.buyerEmail}</FormFeedback>
                                </FormGroup>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
};

export default CustomerInfo;
